// v0.6.2: human-readable explanation for why a scored
// event credited 0 XP/coins. Replaces the catch-all
// 'No awards (scored before evidence tracking)' message
// in the player dashboard's Recent events list.
//
// The data model already has everything we need
// (event.type, payload.review_state, payload.to_status,
// etc.); we just need to surface a message specific to
// the situation AND name the event type that didn't
// match a rule, so the player (or admin reading
// alongside them) knows what rule they're missing.
//
// The most common cases (in the prod data, ranked by
// frequency):
//   1. pr.review.submitted with review_state='commented'
//      → commenting on a PR fires another event when
//        the author pushes; GitHub sends it as
//        review_state='commented' (not approved/
//        changes_requested). No rule matches because
//        'commented' isn't a formal review action. By
//        design, but the message used to be misleading.
//   2. pr.review.submitted with review_state='approved'
//      but no matching rule on the user's team → the
//        team has the kudos rule but not the approval
//        rule. Operator config, not a scoring bug.
//   3. issue.transition with no matrix row + no rule
//      → the team hasn't configured a matrix or rule
//      for this transition.
//   4. pr.opened / pr.closed on a team with no rule →
//      operator config.
//
// The fallback is a generic 'no rule matched' which is
// accurate for the unknown cases without claiming a
// specific cause.

/**
 * Human-readable label for the event type — the same
 * language the player dashboard already uses in the
 * event row (e.g. "PR review submitted", "Issue
 * transition"). Centralised here so the message and
 * the row agree on naming.
 */
function eventTypeLabel(type: string): string {
  const map: Record<string, string> = {
    'pr.opened':              'PR opened',
    'pr.closed':              'PR closed',
    'pr.ready_for_review':    'PR ready for review',
    'pr.review.submitted':    'PR review submitted',
    'issue.created':          'Issue created',
    'issue.commented':        'Issue commented',
    'issue.transition':       'Issue transition',
    'issue.updated':          'Issue updated',
    'quest.completed':        'Quest completed',
    'kudos.given':            'Kudos given',
    'manual.grant':           'Admin grant',
  };
  return map[type] ?? type;
}

/**
 * Extract a short, human-readable identifier for the
 * event from its payload — the same `#113` / `YOLO-6517`
 * pattern the player dashboard's Recent events list
 * uses for the event title. Returns `null` if the
 * payload doesn't carry a recognisable identifier
 * (caller should fall back to a generic message).
 */
function pickEventRef(payload: Record<string, unknown>, type: string): string | null {
  const p = payload ?? {};
  // v0.6.2: when a Jira event has a comment_id, append
  // it after the issue_key so the operator/player can
  // tell two comments on the same ticket apart. A
  // single comment_id='45755' on 'YOLO-6627' renders
  // as 'YOLO-6627 · c45755'. Same for GitHub reviews:
  // review_id='4840659141' on pr_number=2696 renders
  // as '#2696 · r4840659141'. Without the secondary id,
  // two distinct events on the same issue/PR look
  // identical in the player dashboard and the live feed
  // (one of the operator's recurring misconception
  // during v0.6.2 — the dedup key is correctly
  // (event_id, rule_id, competency_id), and each
  // comment_id/review_id is its own event_id, but the
  // rendered event title collapsed to just the issue
  // key or PR number).
  const issueKey = typeof p.issue_key === 'string' && p.issue_key.length > 0 ? p.issue_key : null;
  if (issueKey) {
    const commentId = typeof p.comment_id === 'string' && p.comment_id.length > 0 ? p.comment_id : null;
    return commentId ? `${issueKey} · c${commentId}` : issueKey;
  }
  const prNumber = typeof p.pr_number === 'number' ? p.pr_number : null;
  if (prNumber !== null) {
    const reviewId = typeof p.review_id === 'string' && p.review_id.length > 0 ? p.review_id : null;
    return reviewId ? `#${prNumber} · r${reviewId}` : `#${prNumber}`;
  }
  const questTitle = typeof p.quest_title === 'string' && p.quest_title.length > 0 ? p.quest_title : null;
  if (questTitle) return questTitle;
  if (typeof p.target_external === 'string' && p.target_external.length > 0) {
    return p.target_external.replace(/^@/, '');
  }
  return null;
}

export function explainNoAwards(e: {
  type: string;
  payload: Record<string, unknown>;
  // M5.13: per-rule cooldown block summary, optional
  // for callers that don't have it (the live feed and
  // some tests pass plain {type, payload} shapes).
  cooldown_blocked?: { retry_after_seconds: number; blocked_count: number } | null;
}): string {
  // M5.13: cooldown blocks take priority over the
  // generic "no rule matched" message because they're a
  // deliberate rate-limit, not a missing rule. The user
  // is told they CAN earn XP for this — just not now.
  // Format the retry_after_seconds as a human time:
  //   < 60s  → "in 30 seconds"
  //   < 60m  → "in 5 minutes"
  //   < 24h  → "in 2 hours" (rounded)
  //   >= 24h → "in 1 day"
  // Match the live-feed banner style (compact, no
  // decimals). If no cooldown_blocked record, fall
  // through to the existing event-type-specific cases.
  if (e.cooldown_blocked && e.cooldown_blocked.retry_after_seconds > 0) {
    const ref = pickEventRef(e.payload ?? {}, e.type);
    const label = eventTypeLabel(e.type);
    const header = ref ? `${label} on ${ref}: ` : `${label}: `;
    const secs = e.cooldown_blocked.retry_after_seconds;
    let human: string;
    if (secs < 60) human = `${secs} second${secs === 1 ? '' : 's'}`;
    else if (secs < 3600) {
      const m = Math.ceil(secs / 60);
      human = `${m} minute${m === 1 ? '' : 's'}`;
    }
    else if (secs < 86400) {
      const h = Math.ceil(secs / 3600);
      human = `${h} hour${h === 1 ? '' : 's'}`;
    }
    else {
      const d = Math.ceil(secs / 86400);
      human = `${d} day${d === 1 ? '' : 's'}`;
    }
    const plural = e.cooldown_blocked.blocked_count === 1 ? '' : 's';
    const tally = e.cooldown_blocked.blocked_count > 0
      ? ` (${e.cooldown_blocked.blocked_count} recent attempt${plural} this hour too)`
      : '';
    return `${header}rate-limited. You can earn XP for this again in ${human}${tally}.`;
  }
  const p = e.payload ?? {};
  const label = eventTypeLabel(e.type);
  const ref = pickEventRef(p, e.type);
  // Compose a header that names the event so the player
  // (and any admin reading alongside) knows WHICH event
  // the message is about. Format: "PR review submitted
  // on #113:" — the type label first (matches the
  // event row above), then the ref.
  //
  // When there's no ref, just use the type label ("PR
  // review submitted:") rather than producing "On
  // undefined: ..." text.
  const header = ref ? `${label} on ${ref}: ` : `${label}: `;
  if (e.type === 'pr.review.submitted') {
    const state = typeof p.review_state === 'string' ? p.review_state : null;
    if (state === 'commented') {
      return `${header}commenting on a PR isn\u2019t a formal review (only approvals and change requests credit).`;
    }
    if (state === 'dismissed') {
      return `${header}this review was dismissed by the PR author.`;
    }
    if (state === 'approved') {
      return `${header}your team hasn\u2019t configured a rule for approved reviews. Ask an admin to add a pr.review.submitted rule with review_state = approved on this team.`;
    }
    if (state === 'changes_requested') {
      return `${header}your team hasn\u2019t configured a rule for change-request reviews. Ask an admin to add a pr.review.submitted rule with review_state = changes_requested on this team.`;
    }
  }
  if (e.type === 'issue.transition') {
    const to = typeof p.to_status === 'string' ? p.to_status : null;
    if (to) {
      return `${header}\u201c${to}\u201d isn\u2019t in your team\u2019s transition matrix. Ask an admin to add a status_transition_xp row for to_status = ${to} on this team, or a scoring rule for issue.transition that matches it.`;
    }
    return `${header}this transition isn\u2019t in your team\u2019s transition matrix. Ask an admin to add a status_transition_xp row for this to_status, or a scoring rule.`;
  }
  if (e.type === 'pr.opened') {
    return `${header}your team hasn\u2019t configured a rule for pr.opened events. Ask an admin to add one at /admin/scoring-rules.`;
  }
  if (e.type === 'pr.closed') {
    const merged = p.pr_merged === true;
    return merged
      ? `${header}your team hasn\u2019t configured a rule for merged PRs. Ask an admin to add a pr.closed rule with pr_merged = true at /admin/scoring-rules.`
      : `${header}your team hasn\u2019t configured a rule for closed PRs without merge. Ask an admin to add a pr.closed rule with pr_merged = false at /admin/scoring-rules.`;
  }
  if (e.type === 'kudos.given') {
    const receiver = typeof p.target_external === 'string' ? p.target_external.replace(/^@/, '') : null;
    return receiver
      ? `${header}kudos given doesn\u2019t credit XP to the giver (only ${receiver} gets kudos XP). This is by design — kudos credit the receiver, not the giver.`
      : `${header}kudos given doesn\u2019t credit XP to the giver (only the receiver gets kudos XP). This is by design.`;
  }
  return `${header}no scoring rule matched this event type on your team. Ask an admin to add a rule at /admin/scoring-rules.`;
}